import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Typography,
  useTheme,
} from "@mui/material";
import { SupportButton } from "components/snackbars/SupportButton";
import { ProductFeature, ProductIcon } from "data/models";
import { CircleCheck, Info } from "lucide-react";
import { toTitleCase } from "utils/commons";

// Helper: format dollar amount with commas
const formatPrice = (amount: number) =>
  amount.toLocaleString(undefined, { minimumFractionDigits: 0 });

type ProductCardProps = {
  priceId: string | null;
  title: string;
  dollarCost: number | null;
  cadence: "month" | "year";
  selected: boolean;
  disabled: boolean;
  type: "selectable" | "contact" | "current" | "trial";
  features: ProductFeature[];
  handleSelect: (selected: string) => void;
};

export const ProductCard = (props: ProductCardProps) => {
  const theme = useTheme();

  const getIcon = (iconType: ProductIcon) => {
    if (iconType === "info") {
      return <Info style={{ color: theme.palette.info.main }} />;
    }
    return <CircleCheck style={{ color: theme.palette.success.main }} />;
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        minWidth: 240,
        display: "flex",
        flexDirection: "column",
        backgroundColor: `${
          props.type === "current"
            ? theme.palette.action.hover
            : props.selected
            ? theme.palette.action.selected
            : theme.palette.background.paper
        }`,
      }}
    >
      <CardHeader
        sx={{ margin: "16px 16px 0px 16px" }}
        title={toTitleCase(props.title)}
      ></CardHeader>
      <CardContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
        >
          {props.type === "contact" ? (
            <Typography variant="h4">Contact us</Typography>
          ) : props.dollarCost === null ? (
            <Typography variant="h3">-</Typography>
          ) : (
            <Typography variant="h3">
              ${formatPrice(props.dollarCost)}
              <Typography component="span" variant="body2">
                {" "}
                per {props.cadence}
              </Typography>
            </Typography>
          )}
        </Box>
        {/* Feature list fills available space */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 1,
            minWidth: 347,
          }}
        >
          {props.features
            .filter((x) => !x.cadence || x.cadence === props.cadence)
            .map((f, index) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "2px 16px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {getIcon(f.icon)}
                  <Box sx={{ paddingLeft: 3 }}>
                    <Typography variant="body2">{f.text}</Typography>
                  </Box>
                </Box>
                {f.chip && (
                  <Chip
                    size="small"
                    color={f.chip.colour}
                    label={f.chip.text}
                    variant="outlined"
                  />
                )}
              </Box>
            ))}
        </Box>
      </CardContent>
      <CardActions
        sx={{
          alignSelf: "center",
          marginTop: "auto",
          marginBottom: 1,
        }}
      >
        {props.type === "contact" ? (
          <SupportButton text="Contact" variant="contained" />
        ) : props.selected ? (
          <Button variant="contained" disabled>
            Selected
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={props.disabled}
            onClick={() => props.handleSelect(props.priceId ?? "")}
          >
            {props.type === "selectable" && "Select"}
            {props.type === "trial" && "Trial"}
            {props.type === "current" && "Current"}

          </Button>
        )}
      </CardActions>
    </Card>
  );
};
